// src/components/SummaryDrawer.jsx
import React from "react";
import {
  Box,
  Typography,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import AddToCart from "./AddToCart";
import { usePricing } from "../hooks/usePricing";
import { useData } from "../context/DataContext";
import { getColorsByProductCode } from "../constants/colors";
import { getHardwareByProductCode } from "../constants/hardwares";
import { getFinishByProductCode } from "../constants/finishes";
import { getThicknessByProductCode } from "../constants/thicknesses";

export default function SummaryDrawer({ open, onClose }) {
  const { totalPrice, formatPrice } = usePricing();
  const { lines, selectedProduct } = useData();

  const validLines = lines.filter((line) => line.text?.trim());

  return (
    <Box sx={{ p: 2 }}>
      {validLines.length > 0 ? (
        <List disablePadding>
          {validLines.map((line, index) => {
            // === ALL DERIVED VALUES — SAME LOGIC AS LINE ROW ===
            const sizeName =
              selectedProduct?.sizes?.find((s) => s.code === line.size)?.name ||
              line.size ||
              "—";

            const colorOptions =
              getColorsByProductCode(selectedProduct?.code) || [];
            const colorObj = colorOptions.find(
              (c) => c.code === line.color || c.mivaname === line.color
            );
            const colorName =
              colorObj?.name || colorObj?.label || line.color || "—";

            const hardwareOptions =
              getHardwareByProductCode(selectedProduct?.code) || [];
            const hardwareObj = hardwareOptions.find(
              (h) => h.code === line.hardware
            );
            const hardwareName = hardwareObj?.name || "—";

            const finishOptions =
              getFinishByProductCode(selectedProduct?.code) || [];
            const finishObj = finishOptions.find((f) => f.code === line.finish);
            const finishName = finishObj?.name || "—";

            const thicknessOptions =
              getThicknessByProductCode(selectedProduct?.code) || [];
            const thicknessObj = thicknessOptions.find(
              (t) => t.code === line.thickness
            );
            const thicknessName = thicknessObj?.name || "—";

            // Build dynamic list of attributes (exclude "—" and empty)
            const attributes = [
              sizeName,
              colorName !== "—" ? colorName : null,
              line.font,
              hardwareName !== "—" ? hardwareName : null,
              finishName !== "—" ? finishName : null,
              thicknessName !== "—" ? thicknessName : null,
            ].filter(Boolean);

            return (
              <ListItem key={line.id} disableGutters sx={{ py: 0.75 }}>
                <ListItemText
                  primary={
                    <Typography variant="body2" noWrap fontWeight="medium">
                      Line {index + 1}: {line.text}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {attributes.join(" • ")}
                    </Typography>
                  }
                />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ ml: 2, minWidth: 70, textAlign: "right" }}
                >
                  ${Number(line.price || 0).toFixed(2)}
                </Typography>
              </ListItem>
            );
          })}
        </List>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: "italic", py: 2 }}
        >
          No text added yet.
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mt: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" fontWeight="bold">
            Subtotal
          </Typography>
          <Typography variant="h6" color="success.main" fontWeight="bold">
            {formatPrice(totalPrice)}
          </Typography>
        </Stack>

        <AddToCart />
      </Box>
    </Box>
  );
}
